"use client";

import type { PageHeaderComponent } from "../../types";
import { PageTitle } from "./PageTitle";
import { RuntimeLink } from "../../runtime/RendererRuntime";
import { resolveLinkHref } from "../../utils/links";

/**
 * En-tête de sujet : bandeau blanc PLEINE LARGEUR portant le fil d'Ariane, un
 * sur-titre orange, le titre en bleu Aleo et un chapô. C'est le gabarit des
 * pages-sujets macroéconomiques (/statistiques/{secteur}/{sujet}).
 *
 * Il vit ICI, dans le SDK, et non plus dans le Next.js : les pages CMS et les
 * pages macro rendent désormais le MÊME composant, donc les deux familles ne
 * peuvent plus diverger. Sur une page `layout.mode = "fluid"` le bandeau se
 * cadre lui-même (conteneur 75rem, chaque bloc porte sa gouttière) ; sur une
 * page contrainte, <main> fournit déjà la gouttière — le bandeau ne doit alors
 * PAS ajouter la sienne, sinon le titre se décale d'une gouttière par rapport
 * au corps de page.
 */
function SubjectHeader({
  component,
  layoutMode,
}: {
  component: PageHeaderComponent;
  layoutMode?: "fluid" | "constrained";
}) {
  const { title, eyebrow, description, breadcrumb } = component.content;
  // Une miette INTERMÉDIAIRE sans lien est du texte inerte : c'est un segment
  // d'URL sans page réelle derrière (ex. « Billets et monnaie ») — on la saute.
  // Seule la miette TERMINALE a le droit de ne pas être cliquable : elle nomme
  // la page courante.
  // `breadcrumb` peut ne PAS être un tableau : la valeur « auto » est un marqueur, résolu en
  // vrai fil au déploiement côté serveur. L'aperçu de l'éditeur, lui, rend le contenu SAISI,
  // donc le marqueur tel quel : sans ce garde, le `.map` lève et, faute de barrière d'erreur,
  // tout l'aperçu de la page devient blanc au lieu d'un simple en-tête sans fil.
  const trail = (Array.isArray(breadcrumb) ? breadcrumb : [])
    .map((step) => ({ ...step, resolvedHref: resolveLinkHref(step.href) }))
    .filter((step, index, arr) => step.resolvedHref || index === arr.length - 1);
  // Dès que le fil compte DEUX vrais liens, la miette terminale (la page
  // courante, non cliquable) n'apporte rien : le titre en dessous la répète.
  // Elle n'est conservée que quand le fil n'aurait sinon qu'un seul lien,
  // où elle situe encore la page.
  const clickableCount = trail.filter((step) => step.resolvedHref).length;
  const steps = clickableCount >= 2 ? trail.filter((step) => step.resolvedHref) : trail;
  // Cadre par défaut : les pages-sujets codées (/statistiques/…) rendent ce
  // composant DIRECTEMENT, sans passer par PageRenderer, donc sans layoutMode —
  // elles comptent sur l'auto-cadrage. Seul un "constrained" EXPLICITE (fourni
  // par PageRenderer depuis les réglages de la page CMS) le désactive.
  const frameClass =
    layoutMode === "constrained"
      ? "pt-6 pb-4 md:pt-8 md:pb-5"
      : "mx-auto max-w-[75rem] px-4 pt-6 pb-4 md:px-8 md:pt-8 md:pb-5";
  return (
    /* `bcc-subject-header` : marqueur lu par globals.css pour effondrer le
       padding-top du main « constrained » (même mécanisme que bcc-section-hero) —
       sans lui, gutter de page + padding du main + retrait du bandeau
       s'empilent et l'en-tête flotte trop bas par rapport aux pages-sujets
       codées (/statistiques/…), qui n'ont pas ce padding de main. */
    <section className="bcc-subject-header bg-white">
      {/* Retrait BAS volontairement court : le bandeau et le corps de page sont
          tous deux blancs, donc rien ne matérialise la fin du bandeau. Un
          retrait symétrique au retrait haut laissait un trou blanc sous le
          chapô, puisque le premier bloc apporte déjà sa propre marge haute. */}
      <div className={frameClass}>
        {steps.length ? (
          <nav className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-500">
            {steps.map((step, index) => {
              return (
                <span key={`${step.label}-${index}`} className="flex items-center gap-2">
                  {index > 0 ? <span aria-hidden="true">/</span> : null}
                  {step.resolvedHref ? (
                    <RuntimeLink href={step.resolvedHref} className="text-[#005CA9] hover:underline">
                      {step.label}
                    </RuntimeLink>
                  ) : (
                    <span>{step.label}</span>
                  )}
                </span>
              );
            })}
          </nav>
        ) : null}
        {/* Pas de largeur maximale ici : le bandeau occupe la MÊME colonne
            que les panneaux en dessous (conteneur 75rem). Les bornes
            `max-w-5xl` / `max-w-4xl` faisaient revenir le texte à la ligne
            une centaine de pixels avant le bord droit des panneaux, ce qui se
            lisait comme un défaut d'alignement. */}
        <div className={steps.length ? "mt-7" : ""}>
          {eyebrow ? (
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#E98A00]">{eyebrow}</p>
          ) : null}
          <h1
            /* Mobile un cran sous text-4xl (32px au lieu de 36px) : sur 390px le
               titre de sujet mangeait deux lignes pleines ; le gabarit desktop
               (md:text-5xl) est inchangé. */
            className={`text-[2rem] font-bold leading-tight text-[#005CA9] md:text-5xl ${eyebrow ? "mt-2" : ""}`}
            style={{ fontFamily: "var(--font-aleo)" }}
          >
            {title}
          </h1>
          {/* Même filet ambre que `PageTitle` : le repère de marque du système
              est ainsi présent sur les deux gabarits d'en-tête, pas seulement
              sur le titre simple. */}
          <div className="mt-4 h-1 w-12 rounded-sm bg-[#FFA401]" aria-hidden="true" />
          {description ? (
            // Sur mobile le chapô suit la TYPO DE BASE du site (TextBlockRenderer :
            // 16px, interligne 24px) — même corps et même respiration que les
            // paragraphes de /banque-centrale-institution, pour que toutes les
            // familles de pages lisent pareil. Le format chapô (18px/32px)
            // revient dès la tablette.
            <p className="mt-5 text-base leading-6 text-slate-600 md:text-lg md:leading-8">{description}</p>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/** Page header renderer with main title. The small yellow underline accent
 *  echoes the InformationHub heading so every inner page reads as part of the
 *  same design system — quick brand cue, no extra layout cost. Delegates to the
 *  shared `PageTitle` so authored and migrated pages share one header look.
 *  With `variant: "subject"` it renders the full-width statistics-style band
 *  instead — same component, so the two page families stay aligned. */
export function PageHeaderRenderer({
  component,
  layoutMode,
}: {
  component: PageHeaderComponent;
  layoutMode?: "fluid" | "constrained";
}) {
  const { title, variant } = component.content;
  if (variant === "subject") {
    return <SubjectHeader component={component} layoutMode={layoutMode} />;
  }
  return <PageTitle title={title} />;
}
